import { useMutation, useQueryClient } from 'react-query';

import {
  BookmarkLookupItem,
  BookmarkableType,
  bookmarkStatusQueryKey,
  createBookmark,
} from '@utils/backend-api/bookmarks';

interface UseBookmarkCreateParams {
  userId: number | string | null | undefined;
  bookmarkableType: BookmarkableType;
  contentId: number;
}

interface MutationContext {
  previousStatus?: BookmarkLookupItem;
}

export default function useBookmarkCreate({
  userId,
  bookmarkableType,
  contentId,
}: UseBookmarkCreateParams) {
  const queryClient = useQueryClient();
  const queryKey = bookmarkStatusQueryKey(userId, bookmarkableType, contentId);

  const { mutate, mutateAsync, isLoading, isError } = useMutation(
    async () => createBookmark({ type: bookmarkableType, id: contentId }),
    {
      onMutate: async (): Promise<MutationContext> => {
        await queryClient.cancelQueries(queryKey);
        const previousStatus = queryClient.getQueryData<BookmarkLookupItem>(queryKey);

        queryClient.setQueryData<BookmarkLookupItem>(queryKey, {
          type: bookmarkableType,
          id: contentId,
          bookmarked: true,
          bookmarked_at: previousStatus?.bookmarked_at ?? null,
        });

        return { previousStatus };
      },
      onSuccess: (data) => {
        queryClient.setQueryData<BookmarkLookupItem>(queryKey, {
          type: bookmarkableType,
          id: contentId,
          bookmarked: true,
          bookmarked_at: data.created_at,
        });
      },
      onError: (_error, _variables, context) => {
        if (context?.previousStatus) {
          queryClient.setQueryData(queryKey, context.previousStatus);
          return;
        }

        queryClient.setQueryData<BookmarkLookupItem>(queryKey, {
          type: bookmarkableType,
          id: contentId,
          bookmarked: false,
          bookmarked_at: null,
        });
      },
    }
  );

  return {
    createBookmark: mutate,
    createBookmarkAsync: mutateAsync,
    isCreatingBookmark: isLoading,
    isCreateBookmarkError: isError,
  };
}
